import Query from "@arcgis/core/rest/support/Query.js";
import { loadResource } from "./arcgisMapUtils";
import {
  EXPANSION_SEGMENTS_LAYER_TITLE,
  EXPANSION_SEGMENTS_SERVICE_HINT,
  EXPANSION_WORK_ORDER_FIELD,
  getExpansionSegmentsLayerTitles,
} from "./expansionMapConfig";

export {
  EXPANSION_SEGMENT_WEBMAP_ID,
  EXPANSION_SEGMENTS_LAYER_TITLE,
  EXPANSION_SEGMENTS_LAYER_TITLES,
  EXPANSION_PERMITS_OVERVIEW_LAYER_TITLE,
  EXPANSION_WORK_ORDER_FIELD,
  getExpansionSegmentsLayerTitles,
  loadExpansionSegmentWebMap,
} from "./expansionMapConfig";

const FEATURE_QUERY_PAGE_SIZE = 2000;
const FEATURE_QUERY_MAX = 12000;

function escapeArcgisSqlString(value) {
  return String(value ?? "").replace(/'/g, "''");
}

/** SQL where clause matching work_order on segment features (includes sub-WOs for base orders). */
export function buildWorkOrderWhereClause(workOrder, field = EXPANSION_WORK_ORDER_FIELD) {
  const wo = String(workOrder ?? "").trim();
  if (!wo) return null;
  const col = String(field || EXPANSION_WORK_ORDER_FIELD).trim();
  const escaped = escapeArcgisSqlString(wo);
  if (!escaped.includes(".")) {
    return `(${col} = '${escaped}' OR ${col} LIKE '${escaped}.%')`;
  }
  return `${col} = '${escaped}'`;
}

function isQueryableFeatureLayer(layer) {
  if (!layer) return false;
  return layer.type === "feature" || typeof layer.queryExtent === "function";
}

/** Flatten the map's layer tree (group layers and sublayers included). */
function collectFeatureLayers(map) {
  const out = [];
  const seen = new Set();
  const visit = (layer) => {
    if (!layer || seen.has(layer)) return;
    seen.add(layer);
    if (isQueryableFeatureLayer(layer)) out.push(layer);
    const children = layer.layers || layer.sublayers;
    if (children?.length) {
      for (const child of children) visit(child);
    }
  };

  const layers = typeof map?.allLayers?.toArray === "function" ? map.allLayers.toArray() : [];
  for (const layer of layers) visit(layer);
  return out;
}

export function findFeatureLayerByTitle(map, title) {
  const target = String(title ?? "").trim().toLowerCase();
  if (!map || !target) return null;
  return (
    collectFeatureLayers(map).find(
      (layer) =>
        String(layer.title ?? layer.name ?? "")
          .trim()
          .toLowerCase() === target
    ) || null
  );
}

function layerHasWorkOrderField(layer, field = EXPANSION_WORK_ORDER_FIELD) {
  const target = String(field || EXPANSION_WORK_ORDER_FIELD).toLowerCase();
  if (!target) return null;

  const fields = layer?.fields;
  if (Array.isArray(fields) && fields.length) {
    return fields.some((f) => String(f?.name ?? "").toLowerCase() === target);
  }

  // Some layers expose a fieldsIndex / outFields before the full fields array is ready.
  const index = layer?.fieldsIndex;
  if (index && typeof index.get === "function") {
    try {
      if (index.get(target) || index.get(field)) return true;
    } catch {
      /* ignore */
    }
  }

  const outFields = layer?.outFields;
  if (Array.isArray(outFields) && outFields.length) {
    const hit = outFields.some((name) => String(name ?? "").toLowerCase() === target);
    if (hit) return true;
    if (outFields.includes("*")) return null;
  }

  return null;
}

function layerMatchesSegmentsService(layer) {
  const url = String(layer?.url ?? "").toLowerCase();
  return url.includes(EXPANSION_SEGMENTS_SERVICE_HINT.toLowerCase());
}

/**
 * Rank candidate layers: preferred titles first, then layers that clearly belong to the
 * segments service or expose a work_order field. Layers carrying a definitionExpression
 * are demoted for zoom because their filter hides features and would truncate extent.
 */
function scoreSegmentLayer(layer, titles, field) {
  const title = String(layer?.title ?? layer?.name ?? "")
    .trim()
    .toLowerCase();
  const titleRank = titles.findIndex((t) => t.trim().toLowerCase() === title);
  const hasField = layerHasWorkOrderField(layer, field);
  const onService = layerMatchesSegmentsService(layer);

  // Include any layer that matches a known title, the segments service, or has work_order.
  if (titleRank < 0 && !onService && hasField !== true) return null;

  let score = titleRank >= 0 ? titleRank : titles.length + (onService ? 0 : 1);
  if (String(layer?.definitionExpression ?? "").trim()) score += 100;
  return score;
}

async function loadMapFeatureLayers(map) {
  const candidates = collectFeatureLayers(map);
  await Promise.all(candidates.map((layer) => loadResource(layer).catch(() => null)));
  return candidates;
}

/**
 * Every feature layer in the map that has a work_order attribute (or matches the segments
 * service / known titles). Used for highlight so themed overlays (Status, Contract, etc.)
 * dim together with MAIN instead of drawing full-opacity on top.
 */
export async function resolveWorkOrderLayers(map, options = {}) {
  if (!map) return [];
  const field = options.workOrderField || EXPANSION_WORK_ORDER_FIELD;
  const titles = new Set(
    (options.layerTitle
      ? [options.layerTitle, ...getExpansionSegmentsLayerTitles()]
      : getExpansionSegmentsLayerTitles()
    ).map((t) => t.trim().toLowerCase())
  );

  const candidates = await loadMapFeatureLayers(map);
  return candidates.filter((layer) => {
    const title = String(layer?.title ?? layer?.name ?? "")
      .trim()
      .toLowerCase();
    if (titles.has(title)) return true;
    if (layerMatchesSegmentsService(layer)) return true;
    return layerHasWorkOrderField(layer, field) === true;
  });
}

/** Segment layers that can answer a work-order query, best candidate first (for zoom). */
export async function resolveSegmentLayers(map, options = {}) {
  if (!map) return [];
  const field = options.workOrderField || EXPANSION_WORK_ORDER_FIELD;
  const titles = options.layerTitle
    ? [options.layerTitle, ...getExpansionSegmentsLayerTitles()]
    : getExpansionSegmentsLayerTitles();

  const candidates = await loadMapFeatureLayers(map);

  return candidates
    .map((layer) => ({ layer, score: scoreSegmentLayer(layer, titles, field) }))
    .filter((entry) => entry.score != null)
    .sort((a, b) => a.score - b.score)
    .map((entry) => entry.layer);
}

function unionGeometryExtents(features) {
  let extent = null;
  for (const feature of features) {
    const geom = feature?.geometry;
    if (!geom) continue;
    const next = geom.extent ?? geom;
    if (!next) continue;
    extent = extent ? extent.union(next) : next.clone?.() ?? next;
  }
  return extent;
}

/** Query all features matching where (paginated) so extent includes every geometry. */
async function queryMatchingFeatures(layer, where, field = EXPANSION_WORK_ORDER_FIELD) {
  if (!layer || !where || typeof layer.queryFeatures !== "function") return [];
  await loadResource(layer);

  const features = [];
  let start = 0;
  while (start < FEATURE_QUERY_MAX) {
    const query = new Query({
      where,
      returnGeometry: true,
      outFields: [field],
      num: FEATURE_QUERY_PAGE_SIZE,
      start,
    });
    const result = await layer.queryFeatures(query);
    const batch = result?.features || [];
    features.push(...batch);
    if (batch.length < FEATURE_QUERY_PAGE_SIZE) break;
    start += FEATURE_QUERY_PAGE_SIZE;
  }
  return features;
}

async function queryLayerMatch(layer, where, field) {
  if (!layer || !where) return null;

  try {
    const features = await queryMatchingFeatures(layer, where, field);
    if (features.length) {
      const extent = unionGeometryExtents(features);
      if (extent) return { features, extent, count: features.length };
    }
  } catch {
    /* fall through */
  }

  await loadResource(layer);
  if (typeof layer.queryExtent === "function") {
    try {
      const result = await layer.queryExtent({ where });
      if (result?.count > 0 && result.extent) {
        return { features: [], extent: result.extent, count: result.count };
      }
    } catch {
      /* ignore */
    }
  }

  return null;
}

/** Extra geographic buffer for long thin route extents and line symbology. */
export function inflateExtentForFeatures(extent, options = {}) {
  if (!extent) return null;
  const baseFactor = options.expandFactor ?? 1.28;
  let inflated = extent.expand(baseFactor);

  const width = Math.abs(inflated.width ?? 0);
  const height = Math.abs(inflated.height ?? 0);
  if (width <= 0 || height <= 0) return inflated;

  const minDim = Math.min(width, height);
  const maxDim = Math.max(width, height);
  const elongation = minDim / maxDim;
  if (elongation < 0.2) {
    inflated = inflated.expand(1 + (0.2 - elongation));
  }

  return inflated;
}

/** Pixel padding scaled to the current map view size. */
export function goToPaddingForView(view, options = {}) {
  const floor = options.padding ?? 64;
  const width = Number(view?.width) || 0;
  const height = Number(view?.height) || 0;
  if (width <= 0 || height <= 0) return floor;
  const proportional = Math.round(Math.min(width, height) * 0.12);
  return Math.max(floor, proportional);
}

async function goToWorkOrderTarget(view, match, options = {}) {
  const padding = goToPaddingForView(view, options);
  const inflated = inflateExtentForFeatures(match.extent, options);
  if (!inflated) return false;

  try {
    if (match.features?.length) {
      await view.goTo(match.features, { padding });
      return true;
    }
    await view.goTo({ target: inflated, padding });
    return true;
  } catch {
    try {
      await view.goTo({ target: inflated, padding });
      return true;
    } catch {
      return false;
    }
  }
}

/**
 * Query segment features for workOrder and zoom the map view to their combined extent.
 * Returns true when at least one feature matched.
 */
export async function zoomMapViewToWorkOrder(view, workOrder, options = {}) {
  const wo = String(workOrder ?? "").trim();
  if (!wo || !view?.map) return false;

  const field = options.workOrderField || EXPANSION_WORK_ORDER_FIELD;
  const layers = await resolveSegmentLayers(view.map, options);
  if (!layers.length) return false;

  const where = buildWorkOrderWhereClause(wo, field);
  if (!where) return false;

  const lit = escapeArcgisSqlString(wo);
  const whereClauses = [where, `${field} LIKE '%${lit}%'`];

  for (const clause of whereClauses) {
    for (const layer of layers) {
      try {
        const match = await queryLayerMatch(layer, clause, field);
        if (match?.extent && match.count > 0) {
          return goToWorkOrderTarget(view, match, options);
        }
      } catch {
        /* try next layer */
      }
    }
  }

  return false;
}

/** Dim/blur segment features that do not match workOrder (ArcGIS featureEffect). */
export function applyWorkOrderSegmentHighlight(layer, workOrder, options = {}) {
  if (!layer) return false;
  const enabled = options.enabled !== false;
  if (!enabled) {
    layer.featureEffect = null;
    return true;
  }

  const where = buildWorkOrderWhereClause(workOrder, options.workOrderField);
  if (!where) {
    layer.featureEffect = null;
    return false;
  }

  layer.featureEffect = {
    filter: { where },
    includedEffect: "drop-shadow(0 0 4px rgba(59, 130, 246, 0.85))",
    excludedEffect: "blur(3px) opacity(42%) grayscale(25%)",
  };
  return true;
}

/**
 * Apply the highlight to every map layer that carries a work_order attribute.
 * The web map stacks several themed copies of the segments service (MAIN, Status,
 * Contract, Railroad, Water), so effecting only one leaves the others at full opacity.
 */
export async function applyWorkOrderHighlightToSegments(map, workOrder, options = {}) {
  const layers = await resolveWorkOrderLayers(map, options);
  let applied = 0;
  for (const layer of layers) {
    if (applyWorkOrderSegmentHighlight(layer, workOrder, options)) applied += 1;
  }
  return applied;
}

/** Zoom to work-order features and optionally highlight them on the segments layers. */
export async function focusWorkOrderOnMap(view, workOrder, options = {}) {
  const zoomed = await zoomMapViewToWorkOrder(view, workOrder, options);

  if (options.highlight === false || !String(workOrder ?? "").trim()) return zoomed;

  await applyWorkOrderHighlightToSegments(view?.map, workOrder, {
    ...options,
    enabled: options.highlightEnabled !== false,
  });
  return zoomed;
}
